/**
 * Event-Katalog für Socket.IO (Pfad /socket.io) und den Redis-Kanal `lf:events`.
 *
 * Server → Client: `socket.on(ServerEvent, payload)`.
 * Client → Server: `socket.emit(ClientEvent, payload, ack?)` – Ack-Antwort `{ ok: true, data? } | { ok: false, error }`.
 *
 * Räume (siehe docs/API.md „Gateway"):
 *   user:<userId>       alle Sockets eines Nutzers
 *   server:<serverId>   alle Mitglieder (Ereignisse ohne Kanalbezug: Rollen, Mitglieder, Emojis …)
 *   channel:<channelId> nur Sockets mit VIEW_CHANNEL (Nachrichten, Typing, Reaktionen, Pins)
 *   admin               Plattform-Admins (Bridge-Status, Limits)
 */
import type {
  BanDto,
  BridgeStatusDto,
  ChannelDto,
  CustomStatusDto,
  EmojiDto,
  InviteDto,
  MemberDto,
  MessageDto,
  NotificationDto,
  PlatformLimitsDto,
  PresenceDto,
  ReadStateDto,
  ReadyPayload,
  ReactionDto,
  RoleDto,
  ServerDto,
  ServerFullDto,
  SoundDto,
  StickerDto,
  UserDto,
  UserSettingsDto,
  VoiceStateDto,
  ExportJobDto,
  ScheduledMessageDto,
  CurrentUserDto,
  NotificationSettingDto,
} from './dto';
import type { PresenceStatus } from './enums';
import type { Snowflake } from './snowflake';

export interface ServerToClientEvents {
  /** Direkt nach erfolgreichem Handshake. */
  READY: (p: ReadyPayload) => void;
  /** Gateway verlangt Neuverbindung (z. B. Token läuft ab, Rechte geändert). */
  RECONNECT: (p: { reason: string }) => void;
  /** Sitzung ungültig (Konto gesperrt/abgemeldet) – Client verwirft Token. */
  INVALID_SESSION: (p: { reason: string }) => void;

  // Nachrichten (Raum channel:<id>)
  MESSAGE_CREATE: (p: MessageDto) => void;
  MESSAGE_UPDATE: (p: MessageDto) => void;
  MESSAGE_DELETE: (p: { id: Snowflake; channelId: Snowflake; serverId: Snowflake | null }) => void;
  MESSAGE_DELETE_BULK: (p: { ids: Snowflake[]; channelId: Snowflake; serverId: Snowflake | null }) => void;
  /** Link-Vorschauen wurden nachgeladen (nur embeds geändert). */
  MESSAGE_EMBEDS_UPDATE: (p: { id: Snowflake; channelId: Snowflake; embeds: MessageDto['embeds'] }) => void;
  MESSAGE_REACTION_ADD: (p: { messageId: Snowflake; channelId: Snowflake; userId: Snowflake; reaction: Omit<ReactionDto, 'me' | 'meSuper'>; isSuper: boolean }) => void;
  MESSAGE_REACTION_REMOVE: (p: { messageId: Snowflake; channelId: Snowflake; userId: Snowflake; key: string; isSuper: boolean }) => void;
  MESSAGE_REACTION_REMOVE_ALL: (p: { messageId: Snowflake; channelId: Snowflake; key: string | null }) => void;
  CHANNEL_PINS_UPDATE: (p: { channelId: Snowflake; messageId: Snowflake; pinned: boolean; lastPinAt: string | null }) => void;
  TYPING_START: (p: { channelId: Snowflake; userId: Snowflake; serverId: Snowflake | null; timestamp: number }) => void;

  // Kanäle & Threads
  CHANNEL_CREATE: (p: ChannelDto) => void;
  CHANNEL_UPDATE: (p: ChannelDto) => void;
  CHANNEL_DELETE: (p: { id: Snowflake; serverId: Snowflake | null; parentId: Snowflake | null }) => void;
  /** Mehrere Positionen/Kategorien auf einmal geändert. */
  CHANNEL_POSITIONS_UPDATE: (p: { serverId: Snowflake; positions: { id: Snowflake; position: number; parentId: Snowflake | null }[] }) => void;
  CHANNEL_RECIPIENT_ADD: (p: { channelId: Snowflake; user: UserDto }) => void;
  CHANNEL_RECIPIENT_REMOVE: (p: { channelId: Snowflake; userId: Snowflake }) => void;
  THREAD_CREATE: (p: ChannelDto) => void;
  THREAD_UPDATE: (p: ChannelDto) => void;
  THREAD_DELETE: (p: { id: Snowflake; parentId: Snowflake; serverId: Snowflake }) => void;
  THREAD_MEMBERS_UPDATE: (p: { threadId: Snowflake; added: Snowflake[]; removed: Snowflake[]; memberCount: number }) => void;

  // Server, Rollen, Mitglieder (Raum server:<id>)
  SERVER_CREATE: (p: ServerFullDto) => void;
  SERVER_UPDATE: (p: ServerDto) => void;
  /** Server gelöscht ODER eigener Austritt/Kick/Bann (unavailable=false). */
  SERVER_DELETE: (p: { id: Snowflake; reason: 'deleted' | 'left' | 'kicked' | 'banned' }) => void;
  SERVER_MEMBER_ADD: (p: MemberDto) => void;
  SERVER_MEMBER_UPDATE: (p: MemberDto) => void;
  SERVER_MEMBER_REMOVE: (p: { serverId: Snowflake; userId: Snowflake }) => void;
  SERVER_BAN_ADD: (p: { serverId: Snowflake; ban: BanDto }) => void;
  SERVER_BAN_REMOVE: (p: { serverId: Snowflake; userId: Snowflake }) => void;
  ROLE_CREATE: (p: RoleDto) => void;
  ROLE_UPDATE: (p: RoleDto) => void;
  ROLE_DELETE: (p: { serverId: Snowflake; roleId: Snowflake }) => void;
  ROLE_POSITIONS_UPDATE: (p: { serverId: Snowflake; positions: { id: Snowflake; position: number }[] }) => void;
  EMOJIS_UPDATE: (p: { serverId: Snowflake; emojis: EmojiDto[] }) => void;
  STICKERS_UPDATE: (p: { serverId: Snowflake; stickers: StickerDto[] }) => void;
  SOUNDS_UPDATE: (p: { serverId: Snowflake; sounds: SoundDto[] }) => void;
  INVITE_CREATE: (p: InviteDto) => void;
  INVITE_DELETE: (p: { serverId: Snowflake; code: string }) => void;
  WEBHOOKS_UPDATE: (p: { serverId: Snowflake; channelId: Snowflake }) => void;

  // Nutzer (Raum user:<id> bzw. alle, die den Nutzer sehen)
  USER_UPDATE: (p: UserDto | CurrentUserDto) => void;
  USER_SETTINGS_UPDATE: (p: UserSettingsDto) => void;
  PRESENCE_UPDATE: (p: PresenceDto) => void;
  READ_STATE_UPDATE: (p: ReadStateDto) => void;
  NOTIFICATION_CREATE: (p: NotificationDto) => void;
  NOTIFICATION_READ: (p: { ids: Snowflake[] | 'all' }) => void;
  NOTIFICATION_SETTINGS_UPDATE: (p: NotificationSettingDto) => void;
  SCHEDULED_MESSAGE_UPDATE: (p: ScheduledMessageDto) => void;
  EXPORT_UPDATE: (p: ExportJobDto) => void;

  // Voice (Raum server:<id> bzw. channel:<id> bei DMs)
  // leftChannelId: bisheriger Kanal bei Kanalwechsel; moved: true nur bei Verschieben durch einen Moderator (Client folgt).
  VOICE_STATE_UPDATE: (p: VoiceStateDto & { leftChannelId?: Snowflake | null; moved?: boolean }) => void;
  SOUNDBOARD_PLAY: (p: { channelId: Snowflake; soundId: Snowflake; userId: Snowflake; url: string; volume: number }) => void;

  // Admin (Raum admin)
  BRIDGE_STATUS_UPDATE: (p: BridgeStatusDto) => void;
  PLATFORM_LIMITS_UPDATE: (p: PlatformLimitsDto) => void;
}

export type ServerEventName = keyof ServerToClientEvents;
export type ServerEventPayload<E extends ServerEventName> = Parameters<ServerToClientEvents[E]>[0];

export type AckResponse<T = undefined> = { ok: true; data?: T } | { ok: false; error: { code: string; message: string } };
export type Ack<T = undefined> = (res: AckResponse<T>) => void;

export interface ClientToServerEvents {
  /** Eigene Präsenz setzen (wird auch in User.status gespeichert, außer `afk`). */
  PRESENCE_SET: (p: { status: PresenceStatus; customStatus?: CustomStatusDto | null; afk?: boolean }, ack?: Ack) => void;
  /** Tipp-Anzeige: alle ~8 s erneut senden, solange getippt wird. Prüft SEND_MESSAGES. */
  TYPING_START: (p: { channelId: Snowflake }, ack?: Ack) => void;
  /** Gerade angezeigter Kanal (unterdrückt Push für diesen Kanal, setzt ggf. Ungelesen-Status). */
  CHANNEL_FOCUS: (p: { channelId: Snowflake | null }, ack?: Ack) => void;
  /** Mitgliederliste/Präsenzen eines Servers abonnieren (Lazy-Loading großer Server). */
  SERVER_SUBSCRIBE: (p: { serverId: Snowflake; channelId?: Snowflake }, ack?: Ack<{ presences: PresenceDto[] }>) => void;
  /** Eigene Sprach-Zustände (Beitritt selbst erfolgt per REST POST /voice/channels/:id/join). */
  VOICE_STATE_SET: (p: { selfMute?: boolean; selfDeaf?: boolean; video?: boolean; streaming?: boolean }, ack?: Ack) => void;
  VOICE_LEAVE: (p: Record<string, never>, ack?: Ack) => void;
  /** Zeitmessung/Lebenszeichen auf Anwendungsebene (Socket.IO pingt zusätzlich selbst). */
  PING: (p: { t: number }, ack?: Ack<{ t: number; server: number }>) => void;
}

export type ClientEventName = keyof ClientToServerEvents;
export type ClientEventPayload<E extends ClientEventName> = Parameters<ClientToServerEvents[E]>[0];

export const SERVER_EVENT_NAMES = [
  'READY', 'RECONNECT', 'INVALID_SESSION',
  'MESSAGE_CREATE', 'MESSAGE_UPDATE', 'MESSAGE_DELETE', 'MESSAGE_DELETE_BULK', 'MESSAGE_EMBEDS_UPDATE',
  'MESSAGE_REACTION_ADD', 'MESSAGE_REACTION_REMOVE', 'MESSAGE_REACTION_REMOVE_ALL', 'CHANNEL_PINS_UPDATE', 'TYPING_START',
  'CHANNEL_CREATE', 'CHANNEL_UPDATE', 'CHANNEL_DELETE', 'CHANNEL_POSITIONS_UPDATE', 'CHANNEL_RECIPIENT_ADD', 'CHANNEL_RECIPIENT_REMOVE',
  'THREAD_CREATE', 'THREAD_UPDATE', 'THREAD_DELETE', 'THREAD_MEMBERS_UPDATE',
  'SERVER_CREATE', 'SERVER_UPDATE', 'SERVER_DELETE', 'SERVER_MEMBER_ADD', 'SERVER_MEMBER_UPDATE', 'SERVER_MEMBER_REMOVE',
  'SERVER_BAN_ADD', 'SERVER_BAN_REMOVE', 'ROLE_CREATE', 'ROLE_UPDATE', 'ROLE_DELETE', 'ROLE_POSITIONS_UPDATE',
  'EMOJIS_UPDATE', 'STICKERS_UPDATE', 'SOUNDS_UPDATE', 'INVITE_CREATE', 'INVITE_DELETE', 'WEBHOOKS_UPDATE',
  'USER_UPDATE', 'USER_SETTINGS_UPDATE', 'PRESENCE_UPDATE', 'READ_STATE_UPDATE', 'NOTIFICATION_CREATE', 'NOTIFICATION_READ',
  'NOTIFICATION_SETTINGS_UPDATE', 'SCHEDULED_MESSAGE_UPDATE', 'EXPORT_UPDATE',
  'VOICE_STATE_UPDATE', 'SOUNDBOARD_PLAY', 'BRIDGE_STATUS_UPDATE', 'PLATFORM_LIMITS_UPDATE',
] as const satisfies readonly ServerEventName[];

export const CLIENT_EVENT_NAMES = [
  'PRESENCE_SET', 'TYPING_START', 'CHANNEL_FOCUS', 'SERVER_SUBSCRIBE', 'VOICE_STATE_SET', 'VOICE_LEAVE', 'PING',
] as const satisfies readonly ClientEventName[];

// ---------- Raum-Namen ----------

export const Rooms = {
  user: (id: Snowflake) => `user:${id}`,
  server: (id: Snowflake) => `server:${id}`,
  channel: (id: Snowflake) => `channel:${id}`,
  admin: 'admin',
} as const;

// ---------- Redis-Bus (lf:events) ----------

export const REDIS_EVENTS_CHANNEL = 'lf:events';
/** Interne Steuerbefehle (z. B. Räume eines Nutzers neu berechnen). */
export const REDIS_CONTROL_CHANNEL = 'lf:control';

/**
 * Hülle jedes auf `lf:events` veröffentlichten Ereignisses. Die Brücke lauscht hier mit;
 * `origin = 'bridge'` markiert Ereignisse, die die Brücke selbst ausgelöst hat (Echo-Schutz).
 */
export interface BusEnvelope<E extends ServerEventName = ServerEventName> {
  event: E;
  data: ServerEventPayload<E>;
  rooms: string[];
  /** Server-Kontext (für Filter in Brücke/Webhooks). */
  serverId: Snowflake | null;
  channelId: Snowflake | null;
  origin: 'platform' | 'bridge' | 'webhook' | 'bot' | 'system';
  /** Auslösender Nutzer (falls vorhanden). */
  actorId: Snowflake | null;
  ts: number;
}

export type ControlMessage =
  | { type: 'RESYNC_USER_ROOMS'; userId: Snowflake }
  | { type: 'RESYNC_SERVER_ROOMS'; serverId: Snowflake }
  | { type: 'DISCONNECT_USER'; userId: Snowflake; reason: string }
  /** Sockets, die mit diesem API-Token verbunden sind, trennen (Token widerrufen). */
  | { type: 'DISCONNECT_TOKEN'; tokenId: Snowflake; reason: string }
  /** Sockets, deren Access-Token aus dieser Session stammt (Claim `sid`), trennen (Session abgemeldet/widerrufen). */
  | { type: 'DISCONNECT_SESSION'; sessionId: Snowflake; reason: string }
  | { type: 'LIMITS_CHANGED' };
